import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";

import {
  CHAT_TYPOGRAPHY_STORAGE_KEY,
  DEFAULT_CHAT_TYPOGRAPHY,
  fontOption,
  parseChatTypography,
  sizeOption,
  type ChatFontId,
  type ChatSizeId,
  type ChatTypographyPreference,
} from "./typography";

interface ChatTypographyContextValue {
  preference: ChatTypographyPreference;
  setFont: (font: ChatFontId) => void;
  setSize: (size: ChatSizeId) => void;
  reset: () => void;
  chatStyle: CSSProperties;
}

const ChatTypographyContext = createContext<ChatTypographyContextValue | null>(null);

function initialPreference() {
  if (typeof window === "undefined") return DEFAULT_CHAT_TYPOGRAPHY;
  try {
    return parseChatTypography(window.localStorage.getItem(CHAT_TYPOGRAPHY_STORAGE_KEY));
  } catch {
    return DEFAULT_CHAT_TYPOGRAPHY;
  }
}

export function ChatTypographyProvider({ children }: { children: ReactNode }) {
  const [preference, setPreference] = useState<ChatTypographyPreference>(initialPreference);

  useEffect(() => {
    try {
      window.localStorage.setItem(CHAT_TYPOGRAPHY_STORAGE_KEY, JSON.stringify(preference));
    } catch {
      // Restricted storage must not make the cabinet unusable. The setting
      // still works for the current tab and simply won't persist.
    }
  }, [preference]);

  useEffect(() => {
    const syncAcrossTabs = (event: StorageEvent) => {
      if (event.key === CHAT_TYPOGRAPHY_STORAGE_KEY) {
        setPreference(parseChatTypography(event.newValue));
      }
    };
    window.addEventListener("storage", syncAcrossTabs);
    return () => window.removeEventListener("storage", syncAcrossTabs);
  }, []);

  const setFont = useCallback((font: ChatFontId) => {
    setPreference((current) => ({ ...current, font }));
  }, []);
  const setSize = useCallback((size: ChatSizeId) => {
    setPreference((current) => ({ ...current, size }));
  }, []);
  const reset = useCallback(() => setPreference(DEFAULT_CHAT_TYPOGRAPHY), []);

  const chatStyle = useMemo(() => {
    const font = fontOption(preference.font);
    const size = sizeOption(preference.size);
    return {
      "--chat-font-family": font.css,
      "--chat-font-size": `${size.size}px`,
      "--chat-line-height": String(size.lineHeight),
    } as CSSProperties;
  }, [preference]);

  const value = useMemo(
    () => ({ preference, setFont, setSize, reset, chatStyle }),
    [chatStyle, preference, reset, setFont, setSize]
  );

  return (
    <ChatTypographyContext.Provider value={value}>
      {children}
    </ChatTypographyContext.Provider>
  );
}

export function useChatTypography() {
  const context = useContext(ChatTypographyContext);
  if (!context) throw new Error("useChatTypography must be used inside ChatTypographyProvider");
  return context;
}
